import { getOverride } from "../helpers";

/**
 * Admin overrides (`*_override`) for the two map widgets, shared by ResultsMapWidget and ResultsMapOverlayWidget.
 * Values are read with helpers.getOverride, like the live ticker and universal widgets do.
 *
 * Editors set overrides on the race and on its race-level candidates only. County reporting units
 * (`race.reportingUnits[]`) have no override fields, so county numbers stay raw. County candidates only
 * borrow the DISPLAY fields (name, party) of the matching race-level candidate, so one candidate reads the same
 * everywhere (header, summary cards, table columns, tooltip, panel, party colors).
 * A cleared override is null or absent in the API (the admin sends null, the server drops the key), so plain
 * getOverride semantics apply and nothing here guards against blanks.
 */
const rawName = (c) => c.name || [c.first || c.firstName, c.last || c.lastName].filter(Boolean).join(" ");
const GENERATIONAL_SUFFIXES = new Set(["jr", "sr", "ii", "iii", "iv", "v", "esq"]);
const stripPunctuation = (token) => token.replace(/^[`'",.;]+|[`'",.;]+$/g, "");
/**
 * Short label from a full name: the last token that has a letter and is not a generational suffix
 * ("Donald Trump 1" -> "Trump", "Rick Jackson Jr." -> "Jackson"), without stray edge punctuation.
 * Falls back to the whole name when no token qualifies. Normal names give the last word, as before.
 */
const lastWord = (name) => {
  const full = String(name).trim();
  const tokens = full.split(/\s+/).map(stripPunctuation);
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (/\p{L}/u.test(tokens[i]) && !GENERATIONAL_SUFFIXES.has(tokens[i].toLowerCase())) return tokens[i];
  }
  return full;
};
/**
 * Short candidate label (last name only): the candidate's own `last` when it holds a letter and the name
 * is not overridden (applyDisplayOverrides sets matchName only then), else derived from the (overridden) full name.
 * Used by the table column headers, Margin text and tooltips. This is the one place that decides a candidate's short
 * label. Race-level candidates may have no `last`; when it is
 * absent the label falls back to the last word of the name.
 */
export const getShortCandidateLabel = (c) => {
  if (!c) return "";
  const own = c.last;
  if (own && /\p{L}/u.test(own) && c.matchName === undefined) return own;
  return c.name ? lastWord(c.name) : own || "";
};

/** getOverride(c, field), with the `${field}_override` taken from the race-level candidate `rc`. */
const getOverrideFrom = (rc, c, field) =>
  getOverride({ ...c, [`${field}_override`]: rc[`${field}_override`] ?? null }, field);

/** Race title: name_override when set, else exactly what the maps showed before (officeName, then name). */
export const getRaceTitle = (race) => getOverride({ ...race, name: race.officeName || race.name || "" }, "name");

/**
 * The race-level candidate (the one that carries the overrides) that `c` represents.
 * Matched by stable id first (ids can be numbers on county rows and strings on the race), then by the
 * RAW name, never by a displayed (possibly overridden) name.
 */
export const findRaceCandidate = (race, c) => {
  const raceCandidates = race.candidates || [];
  if (c.id != null) {
    const byId = raceCandidates.find((rc) => rc.id != null && String(rc.id) === String(c.id));
    if (byId) return byId;
  }
  const name = rawName(c).toLowerCase();
  if (!name) return null;
  return raceCandidates.find((rc) => rawName(rc).toLowerCase() === name) || null;
};

/**
 * Display fields from the race-level candidate `rc`: name_override and party_override.
 * `last` is only replaced when the candidate already had one (derived from the overridden name), so
 * the short label stays in step with the overridden name (see getShortCandidateLabel).
 */
export const applyDisplayOverrides = (c, rc) => {
  if (!rc) return c;
  const name = getOverrideFrom(rc, c, "name");
  const party = getOverrideFrom(rc, c, "party");
  if (name === c.name && party === c.party) return c;
  const out = { ...c, party };
  if (name !== c.name) {
    // matchName keeps the un-overridden name so candidates can still be matched when two end up with the same displayed name.
    Object.assign(out, { name, matchName: c.name, ...(c.last ? { last: lastWord(name) } : {}) });
  }
  return out;
};

/**
 * Statewide candidate: display overrides plus votes, percentage and winner overrides.
 * Like the universal widget, percentage_override is shown as-is and nothing is recomputed from overridden votes.
 */
export const applyStatewideOverrides = (c, rc) => {
  if (!rc) return c;
  return {
    ...applyDisplayOverrides(c, rc),
    votes: getOverrideFrom(rc, c, "votes"),
    percentage: getOverrideFrom(rc, c, "percentage"),
    winner: getOverrideFrom(rc, c, "winner") === true,
  };
};
